import { call, put, takeLatest, select } from 'redux-saga/effects';

import { types, domainType } from '../constants';
import * as hazardsApi from '../api/hazards';
import * as attachmentApi from '../api/attachment';

import { submitMode } from '../constants';
import {
  getRecordAttachmentsToBeAdded,
  getRecordAttachmentsToBeDeleted
} from '../utils/attachmentUtils';

function* getLoggedInUserId() {
  const extractLoggedInUserId = (state) => state.users.user.item;

  const loggedInUser = yield select(extractLoggedInUserId);
  const loggedInUserId = loggedInUser && loggedInUser.id;
  return loggedInUserId;
}

function* watchFetchAllHazardsForBranch({ branchId }) {
  try {
    yield put({ type: types.START_FETCH_ALL_HAZARDS_FOR_BRANCH });

    const { jsonData } = yield call(hazardsApi.getHazardsByBranch, branchId);

    yield put({ type: types.CLEAR_HAZARD_FILTERS });
    yield put({
      type: types.FETCH_ALL_HAZARDS_FOR_BRANCH_SUCCESS,
      hazards: jsonData
    });
  } catch (e) {
    yield resolveError(types.FETCH_ALL_HAZARDS_FOR_BRANCH_FAILURE, e);
  } finally {
    yield put({ type: types.FETCH_ALL_HAZARDS_FOR_BRANCH_FINISHED });
  }
}

function* watchFetchAllHazardsForCompany({ companyId }) {
  try {
    yield put({ type: types.START_FETCH_ALL_HAZARDS_FOR_COMPANY });
    const { jsonData } = yield call(hazardsApi.getHazardsByCompany, companyId);
    yield put({
      type: types.FETCH_ALL_HAZARDS_FOR_COMPANY_SUCCESS,
      hazards: jsonData
    });
  } catch (e) {
    yield resolveError(types.FETCH_ALL_HAZARDS_FOR_COMPANY_FAILURE, e);
  } finally {
    yield put({ type: types.FETCH_ALL_HAZARDS_FOR_COMPANY_FINISHED });
  }
}

function* watchCreateEditHazard({ hazard, mode }) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({ type: types.START_SUBMIT_HAZARD });
    const { jsonData } =
      mode === submitMode.CREATE
        ? yield call(hazardsApi.createHazard, hazard, loggedInUserId)
        : yield call(hazardsApi.editHazard, hazard, loggedInUserId);

    const domainTypeId = domainType.HAZARD;

    const branchId = hazard.branchId;

    //Attachments
    //upload attachments if there is any

    const attachments = getRecordAttachmentsToBeAdded(hazard);
    if (typeof attachments !== 'undefined' && attachments.length > 0) {
      let parentId = jsonData.id;
      yield put({
        type: types.UPLOAD_ATTACHMENTS_FOR_HAZARD,
        domainTypeId,
        parentId,
        branchId,
        attachments
      });
    }

    //Delete attachments if any
    const attachmentToDelete = getRecordAttachmentsToBeDeleted(hazard);
    if (
      typeof attachmentToDelete !== 'undefined' &&
      attachmentToDelete.length > 0
    ) {
      let attachmentIdsArray = attachmentToDelete.map((g) => g.id);
      yield put({
        type: types.DELETE_ATTACHMENTS_FOR_HAZARD,
        attachmentIdsArray,
        loggedInUserId,
        branchId
      });
    }

    yield put({
      type: types.SUBMIT_HAZARD_SUCCESS,
      submittedHazard: jsonData
    });

    if (mode === submitMode.CREATE) {
      yield put({ type: types.OPEN_CREATE_HAZARD_DIALOG, open: false });
    } else {
      yield put({ type: types.OPEN_EDIT_HAZARD_DIALOG, open: false });
    }
  } catch (e) {
    yield resolveError(types.SUBMIT_HAZARD_FAILURE, e);
  } finally {
    yield put({ type: types.SUBMIT_HAZARD_FINISHED });
  }
}

function* watchDeleteHazard({ hazard }) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    hazard.status = 4;
    yield put({ type: types.START_DELETE_HAZARD });
    const { jsonData } = yield call(
      hazardsApi.editHazard,
      hazard,
      loggedInUserId
    );
    yield put({
      type: types.DELETE_HAZARD_SUCCESS,
      hazardId: hazard.id
    });
    yield put({ type: types.OPEN_DELETE_HAZARD_DIALOG, open: false });
  } catch (e) {
    yield resolveError(types.DELETE_HAZARD_FAILURE, e);
  } finally {
    yield put({ type: types.DELETE_HAZARD_FINISHED });
  }
}

function* watchFetchControlsForTheHazard({ hazardId }) {
  try {
    yield put({ type: types.START_FETCH_CONTROLS_FOR_THE_HAZARD });

    const { jsonData } = yield call(hazardsApi.getControlsForHazard, hazardId);
    yield put({
      type: types.FETCH_CONTROLS_FOR_THE_HAZARD_SUCCESS,
      controls: jsonData
    });
  } catch (e) {
    yield resolveError(types.FETCH_CONTROLS_FOR_THE_HAZARD_FAILURE, e);
  } finally {
    yield put({ type: types.FETCH_CONTROLS_FOR_THE_HAZARD_FINISHED });
  }
}

function* watchDeleteHazardControl({ payload }) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({ type: types.START_DELETE_HAZARD_CONTROL });
    yield call(hazardsApi.deleteHazardControl, payload, loggedInUserId);

    yield put({
      type: types.DELETE_HAZARD_CONTROL_SUCCESS,
      controlId: payload.id
    });
    yield put({
      type: types.OPEN_DELETE_HAZARD_CONTROL_DIALOG,
      open: false,
      controlId: payload.id
    });
  } catch (e) {
    yield resolveError(types.DELETE_HAZARD_CONTROL_FAILURE, e);
  } finally {
    yield put({ type: types.DELETE_HAZARD_CONTROL_FINISHED });
  }
}

function* watchSubmitHazardControl({ payload, mode, branchId }) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({ type: types.START_SUBMIT_HAZARD_CONTROL });

    const { jsonData } =
      mode === submitMode.CREATE
        ? yield call(hazardsApi.createControl, payload, loggedInUserId)
        : yield call(hazardsApi.updateControl, payload, loggedInUserId);

    yield put({
      type: types.SUBMIT_HAZARD_CONTROL_SUCCESS,
      createdControl: jsonData
    });

    yield put({
      type: types.FETCH_CONTROLS_FOR_THE_HAZARD,
      hazardId: payload.hazardId
    });

    if (mode === submitMode.UPDATE) {
      yield put({
        type: types.ENABLE_EDIT_HAZARD_CONTROL,
        enableEditControlMode: false,
        controlId: payload.id
      });
    } else {
      yield put({
        type: types.OPEN_ADD_HAZARD_CONTROL_DIALOG,
        open: false
      });
    }
  } catch (e) {
    yield resolveError(types.SUBMIT_HAZARD_CONTROL_FAILURE, e);
  } finally {
    yield put({ type: types.SUBMIT_HAZARD_CONTROL_FINISHED });
  }
}

//Review

function* watchFetchReviewsForTheHazard({ hazardId }) {
  try {
    yield put({ type: types.START_FETCH_REVIEWS_FOR_THE_HAZARD });

    const { jsonData } = yield call(hazardsApi.getReviewsForHazard, hazardId);
    yield put({
      type: types.FETCH_REVIEWS_FOR_THE_HAZARD_SUCCESS,
      reviews: jsonData
    });
  } catch (e) {
    yield resolveError(types.FETCH_REVIEWS_FOR_THE_HAZARD_FAILURE, e);
  } finally {
    yield put({ type: types.FETCH_REVIEWS_FOR_THE_HAZARD_FINISHED });
  }
}

function* watchSubmitHazardReview({ payload, mode, branchId }) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({ type: types.START_SUBMIT_HAZARD_REVIEW });

    const { jsonData } =
      mode === submitMode.CREATE
        ? yield call(hazardsApi.createReview, payload, loggedInUserId)
        : yield call(hazardsApi.updateReview, payload, loggedInUserId);

    const domainTypeId = domainType.HAZARD_REVIEW;

    //Attachments
    //upload attachments if there is any
    const attachments = getRecordAttachmentsToBeAdded(payload);
    if (typeof attachments !== 'undefined' && attachments.length > 0) {
      let parentId = jsonData.id;
      yield put({
        type: types.UPLOAD_ATTACHMENTS_FOR_HAZARD,
        domainTypeId,
        parentId,
        branchId,
        attachments
      });
    }

    //Delete attachments if any
    const attachmentToDelete = getRecordAttachmentsToBeDeleted(payload);
    if (
      typeof attachmentToDelete !== 'undefined' &&
      attachmentToDelete.length > 0
    ) {
      let attachmentIdsArray = attachmentToDelete.map((g) => g.id);
      yield put({
        type: types.DELETE_ATTACHMENTS_FOR_HAZARD,
        attachmentIdsArray,
        loggedInUserId,
        branchId
      });
    }

    yield put({
      type: types.SUBMIT_HAZARD_REVIEW_SUCCESS,
      createdReview: jsonData
    });

    yield put({
      type: types.FETCH_REVIEWS_FOR_THE_HAZARD,
      hazardId: payload.hazardId
    });

    if (mode === submitMode.UPDATE) {
      if (payload.isActive === false) {
        yield put({
          type: types.OPEN_DELETE_HAZARD_REVIEW_DIALOG,
          open: false,
          reviewId: payload.id
        });
      } else {
        yield put({
          type: types.ENABLE_EDIT_HAZARD_REVIEW,
          enableEditReviewMode: false,
          reviewId: payload.id
        });
      }
    } else {
      yield put({
        type: types.OPEN_CREATE_HAZARD_REVIEW_DIALOG,
        open: false
      });
    }
  } catch (e) {
    yield resolveError(types.SUBMIT_HAZARD_REVIEW_FAILURE, e);
  } finally {
    yield put({ type: types.SUBMIT_HAZARD_REVIEW_FINISHED });
  }
}

//ManagementLog

function* watchFetchManagementLogsForTheHazard({ hazardId }) {
  try {
    yield put({ type: types.START_FETCH_MANAGEMENT_LOGS_FOR_THE_HAZARD });

    const { jsonData } = yield call(
      hazardsApi.getManagementLogsForHazard,
      hazardId
    );
    yield put({
      type: types.FETCH_MANAGEMENT_LOGS_FOR_THE_HAZARD_SUCCESS,
      managementLogs: jsonData
    });
  } catch (e) {
    yield resolveError(types.FETCH_MANAGEMENT_LOGS_FOR_THE_HAZARD_FAILURE, e);
  } finally {
    yield put({ type: types.FETCH_MANAGEMENT_LOGS_FOR_THE_HAZARD_FINISHED });
  }
}

function* watchSubmitHazardManagementLog({ payload, mode, branchId }) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({ type: types.START_SUBMIT_HAZARD_MANAGEMENT_LOG });

    const { jsonData } =
      mode === submitMode.CREATE
        ? yield call(hazardsApi.createManagementLog, payload, loggedInUserId)
        : yield call(hazardsApi.updateManagementLog, payload, loggedInUserId);

    const domainTypeId = domainType.HAZARD_LOGS;

    //Attachments
    //upload attachments if there is any
    const attachments = getRecordAttachmentsToBeAdded(payload);
    if (typeof attachments !== 'undefined' && attachments.length > 0) {
      let parentId = jsonData.id;
      yield put({
        type: types.UPLOAD_ATTACHMENTS_FOR_HAZARD,
        domainTypeId,
        parentId,
        branchId,
        attachments
      });
    }

    //Delete attachments if any
    const attachmentToDelete = getRecordAttachmentsToBeDeleted(payload);
    if (
      typeof attachmentToDelete !== 'undefined' &&
      attachmentToDelete.length > 0
    ) {
      let attachmentIdsArray = attachmentToDelete.map((g) => g.id);
      yield put({
        type: types.DELETE_ATTACHMENTS_FOR_HAZARD,
        attachmentIdsArray,
        loggedInUserId,
        branchId
      });
    }

    yield put({
      type: types.SUBMIT_HAZARD_MANAGEMENT_LOG_SUCCESS,
      createdManagementLog: jsonData
    });

    yield put({
      type: types.FETCH_MANAGEMENT_LOGS_FOR_THE_HAZARD,
      hazardId: payload.hazardId
    });

    if (mode === submitMode.UPDATE) {
      if (payload.isActive === false) {
        yield put({
          type: types.OPEN_DELETE_HAZARD_MANAGEMENT_LOG_DIALOG,
          open: false,
          managementLogId: payload.id
        });
      } else {
        yield put({
          type: types.ENABLE_EDIT_HAZARD_MANAGEMENT_LOG,
          enableEditReviewMode: false,
          managementLogId: payload.id
        });
      }
    } else {
      yield put({
        type: types.OPEN_CREATE_HAZARD_MANAGEMENT_LOG_DIALOG,
        open: false
      });
    }
  } catch (e) {
    yield resolveError(types.SUBMIT_HAZARD_MANAGEMENT_LOG_FAILURE, e);
  } finally {
    yield put({ type: types.SUBMIT_HAZARD_MANAGEMENT_LOG_FINISHED });
  }
}

//attachments
function* watchLoadAttachmentFilesForHazard({
  domainTypeId,
  parentId,
  loggedInUserId
}) {
  try {
    yield put({ type: types.START_LOAD_ATTACHMENTS_FOR_HAZARD });
    const { jsonData } = yield call(
      attachmentApi.getAttachmentsForSingle,
      domainTypeId,
      parentId,
      loggedInUserId
    );
    yield put({
      type: types.LOAD_ATTACHMENTS_FOR_HAZARD_SUCCESS,
      attachments: jsonData
    });
  } catch (error) {
    yield resolveError(types.LOAD_ATTACHMENTS_FOR_HAZARD_FAILURE, error);
  } finally {
    yield put({ type: types.LOAD_ATTACHMENTS_FOR_HAZARD_FINISHED });
  }
}

function* watchDeleteAttachmentsForHazard({
  attachmentIdsArray,
  loggedInUserId,
  branchId
}) {
  try {
    if (
      typeof attachmentIdsArray !== 'undefined' &&
      attachmentIdsArray.length > 0
    ) {
      const attachmentIdsString = attachmentIdsArray.join(';');
      yield call(
        attachmentApi.deleteAttachments,
        attachmentIdsString,
        loggedInUserId,
        branchId
      );
    }
  } catch (error) {
  } finally {
  }
}

function* watchUploadAttachmentsForHazard({
  domainTypeId,
  parentId,
  branchId,
  attachments
}) {
  try {
    yield call(
      attachmentApi.addAttachments,
      domainTypeId,
      parentId,
      branchId,
      attachments
    );
  } catch (error) {
  } finally {
  }
}

function* watchLoadAttachmentFilesForHazardReview({
  domainTypeId,
  parentId,
  userId
}) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({ type: types.START_LOAD_ATTACHMENTS_FOR_HAZARD_REVIEW });
    const { jsonData } = yield call(
      attachmentApi.getAttachmentsForSingle,
      domainTypeId,
      parentId,
      loggedInUserId
    );
    yield put({
      type: types.LOAD_ATTACHMENTS_FOR_HAZARD_REVIEW_SUCCESS,
      attachments: jsonData
    });
  } catch (error) {
    yield resolveError(types.LOAD_ATTACHMENTS_FOR_HAZARD_REVIEW_FAILURE, error);
  } finally {
    yield put({ type: types.LOAD_ATTACHMENTS_FOR_HAZARD_REVIEW_FINISHED });
  }
}

function* watchLoadAttachmentFilesForHazardManagementLogs({
  domainTypeId,
  parentId,
  userId
}) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({
      type: types.START_LOAD_ATTACHMENTS_FOR_HAZARD_MANAGEMENT_LOGS
    });
    const { jsonData } = yield call(
      attachmentApi.getAttachmentsForSingle,
      domainTypeId,
      parentId,
      loggedInUserId
    );
    yield put({
      type: types.LOAD_ATTACHMENTS_FOR_HAZARD_MANAGEMENT_LOGS_SUCCESS,
      attachments: jsonData
    });
  } catch (error) {
    yield resolveError(
      types.LOAD_ATTACHMENTS_FOR_HAZARD_MANAGEMENT_LOGS_FAILURE,
      error
    );
  } finally {
    yield put({
      type: types.LOAD_ATTACHMENTS_FOR_HAZARD_MANAGEMENT_LOGS_FINISHED
    });
  }
}

//Hazard control load attachment
function* watchLoadAttachmentFilesForHazardControl({
  domainTypeId,
  parentId,
  userId
}) {
  try {
    const loggedInUserId = yield getLoggedInUserId();
    yield put({ type: types.START_LOAD_ATTACHMENTS_FOR_HAZARD_CONTROL });
    const { jsonData } = yield call(
      attachmentApi.getAttachmentsForSingle,
      domainTypeId,
      parentId,
      loggedInUserId
    );

    yield put({
      type: types.LOAD_ATTACHMENTS_FOR_HAZARD_CONTROL_SUCCESS,
      attachments: jsonData
    });
  } catch (error) {
    yield resolveError(
      types.LOAD_ATTACHMENTS_FOR_HAZARD_CONTROL_FAILURE,
      error
    );
  } finally {
    yield put({ type: types.LOAD_ATTACHMENTS_FOR_HAZARD_CONTROL_FINISHED });
  }
}

//This is used to catch all the errors in case of request unsuccessful, and will extract the actual error message.
function* resolveError(type, error) {
  let errorMessageCaptured = false;
  let completeErrorMessage = {};
  if (!error.ok) {
    try {
      const clone = error.clone();
      yield clone.text().then((text) => {
        completeErrorMessage = JSON.parse(text);
        errorMessageCaptured = true;
      });
    } catch (err) {
      errorMessageCaptured = true;
      completeErrorMessage = err;
    }

    if (errorMessageCaptured) {
      const errorMessage = extractErrorMessage(completeErrorMessage);
      yield put({ type, errorMessage });
    }
  }
}

function extractErrorMessage(completeErrorMessage) {
  let errorMessage = '';

  try {
    const message = completeErrorMessage.exceptionMessage
      ? completeErrorMessage.exceptionMessage.split('Content:')[1]
      : completeErrorMessage.messageDetail;

    errorMessage = JSON.parse(message).errorMessage;
    if (!errorMessage) {
      errorMessage = 'An error occured';
    }
  } catch (error) {
    errorMessage = 'An error occured';
  }

  return errorMessage;
}
export default function* BranchesWatcher() {
  yield takeLatest(
    types.FETCH_ALL_HAZARDS_FOR_BRANCH,
    watchFetchAllHazardsForBranch
  );
  yield takeLatest(
    types.FETCH_ALL_HAZARDS_FOR_COMPANY,
    watchFetchAllHazardsForCompany
  );
  yield takeLatest(types.SUBMIT_HAZARD, watchCreateEditHazard);
  yield takeLatest(types.DELETE_HAZARD, watchDeleteHazard);

  yield takeLatest(
    types.FETCH_CONTROLS_FOR_THE_HAZARD,
    watchFetchControlsForTheHazard
  );

  yield takeLatest(types.DELETE_HAZARD_CONTROL, watchDeleteHazardControl);
  yield takeLatest(types.SUBMIT_HAZARD_CONTROL, watchSubmitHazardControl);

  yield takeLatest(
    types.FETCH_REVIEWS_FOR_THE_HAZARD,
    watchFetchReviewsForTheHazard
  );

  yield takeLatest(types.SUBMIT_HAZARD_REVIEW, watchSubmitHazardReview);

  yield takeLatest(
    types.FETCH_MANAGEMENT_LOGS_FOR_THE_HAZARD,
    watchFetchManagementLogsForTheHazard
  );

  yield takeLatest(
    types.SUBMIT_HAZARD_MANAGEMENT_LOG,
    watchSubmitHazardManagementLog
  );

  yield takeLatest(
    types.UPLOAD_ATTACHMENTS_FOR_HAZARD,
    watchUploadAttachmentsForHazard
  );

  yield takeLatest(
    types.DELETE_ATTACHMENTS_FOR_HAZARD,
    watchDeleteAttachmentsForHazard
  );

  yield takeLatest(
    types.LOAD_ATTACHMENTS_FOR_HAZARD,
    watchLoadAttachmentFilesForHazard
  );

  yield takeLatest(
    types.LOAD_ATTACHMENTS_FOR_HAZARD_REVIEW,
    watchLoadAttachmentFilesForHazardReview
  );

  yield takeLatest(
    types.LOAD_ATTACHMENTS_FOR_HAZARD_MANAGEMENT_LOGS,
    watchLoadAttachmentFilesForHazardManagementLogs
  );

  yield takeLatest(
    types.LOAD_ATTACHMENTS_FOR_HAZARD_CONTROL,
    watchLoadAttachmentFilesForHazardControl
  );
}
